import { ApiPattern } from "../offline-asmt-data-service/offline-asmt-data-interface";

export enum SQLiteProgress {
  Started,
  Finished,
  Failed,
}

export const OFFLINE_ACTION_VALIDITY_IN_DAYS = 14;

export const OFFLINE_ACTION_DATE_FORMAT: string = "YYYYMMDDTHHmmssZZ";

export const DB_DATE_FORMAT: string = "MMM DD, YYYY H:mm:ss A";

export const OFFLINE_ASSESSMENT_ID_INITIAL = 900000;

export const SYNC_SUCCESS_MSG = "All offline changes synced successfully.";

export const NAVIGATION_SECTION_NAME = "navigation";

export const OFFLINE_APIS = {
  users: "users",
  lookupAssessmentSection: "lookupAssessmentSection",
  lookupVisitPage: "lookupVisitPage",
  visits: "visits",
  profile: "profile",
  visitFrequencies: "visitfrequencies",
  clinicalOrderItems: "clinicalOrderItems",
  assessment: "assessment",
  assessmentType: "assessmentType",
  assessmentDetails: "assessmentDetails",
  assessmentNavigation: "assessmentNavigation",
  assessmentSectionTemplate: "assessmentSectionTemplate",
  assessmentSectionData: "assessmentSectionData",
  assessmentSectionCard: "assessmentSectionCard",
  assessmentSectionCardEntry: "assessmentSectionCardEntry",
  patientInfoFromQBOSId: "patientInfoFromQBOSId",
};

export const APIS_PATTERN_REGEX = {
  users: /users\/$/,
  lookupAssessmentSection: /lookup\/domains\/assessment_section$/,
  lookupVisitPage: /users\/permissions\/visit_page$/,
  visits: /patient-charts\/\d+\/visits/,
  profile: /patients\/\d+\/profiles/,
  visitFrequencies: /patient-charts\/\d+\/visitfrequencies/,
  clinicalOrderItems: /clinical\/orders\/items$/,
  assessment: /visits\/$/,
  assessmentType: /patient-charts\/\d+\/visit-types/,
  assessmentDetails: /visits\/\d+$/,
  assessmentNavigation: /visits\/\d+\/template\/navigation$/,
  assessmentSectionTemplate: /visits\/\d+\/template\/.+$/,
  assessmentSectionCardEntry: /visits\/\d+\/sections\/.+\/cards\/.+\/.+$/,
  assessmentSectionData: /visits\/\d+\/sections\/.+$/,
  assessmentSectionCard: /visits\/\d+\/sections\/.+\/cards\/.+$/,
  patientInfoFromQBOSId: /clinical\/company\/patients\/qbosid\/\d+$/,
};

// API list which will get serve in offline mode
export const OFFLINE_API_PATTERNS: ApiPattern[] = [
  { apiPath: OFFLINE_APIS.users, pattern: APIS_PATTERN_REGEX.users },
  {
    apiPath: OFFLINE_APIS.lookupAssessmentSection,
    pattern: APIS_PATTERN_REGEX.lookupAssessmentSection,
  },
  { apiPath: OFFLINE_APIS.visits, pattern: APIS_PATTERN_REGEX.visits },
  { apiPath: OFFLINE_APIS.profile, pattern: APIS_PATTERN_REGEX.profile },
  {
    apiPath: OFFLINE_APIS.visitFrequencies,
    pattern: APIS_PATTERN_REGEX.visitFrequencies,
  },
  { apiPath: OFFLINE_APIS.assessment, pattern: APIS_PATTERN_REGEX.assessment },
  {
    apiPath: OFFLINE_APIS.assessmentType,
    pattern: APIS_PATTERN_REGEX.assessmentType,
  },
  {
    apiPath: OFFLINE_APIS.lookupVisitPage,
    pattern: APIS_PATTERN_REGEX.lookupVisitPage,
  },
  {
    apiPath: OFFLINE_APIS.clinicalOrderItems,
    pattern: APIS_PATTERN_REGEX.clinicalOrderItems,
  },
  {
    apiPath: OFFLINE_APIS.assessmentDetails,
    pattern: APIS_PATTERN_REGEX.assessmentDetails,
  },
  {
    apiPath: OFFLINE_APIS.assessmentNavigation,
    pattern: APIS_PATTERN_REGEX.assessmentNavigation,
  },
  {
    apiPath: OFFLINE_APIS.assessmentSectionTemplate,
    pattern: APIS_PATTERN_REGEX.assessmentSectionTemplate,
  },
  {
    apiPath: OFFLINE_APIS.assessmentSectionCardEntry,
    pattern: APIS_PATTERN_REGEX.assessmentSectionCardEntry,
  },
  {
    apiPath: OFFLINE_APIS.assessmentSectionCard,
    pattern: APIS_PATTERN_REGEX.assessmentSectionCard,
  },
  {
    apiPath: OFFLINE_APIS.assessmentSectionData,
    pattern: APIS_PATTERN_REGEX.assessmentSectionData,
  },
  {
    apiPath: OFFLINE_APIS.patientInfoFromQBOSId,
    pattern: APIS_PATTERN_REGEX.patientInfoFromQBOSId,
  },
];

// API list to store response as it in SQLite (static data to be used in offline mode)
export const STATIC_API_PATTERNS: ApiPattern[] = [
  { apiPath: OFFLINE_APIS.users, pattern: APIS_PATTERN_REGEX.users },
  {
    apiPath: OFFLINE_APIS.lookupAssessmentSection,
    pattern: APIS_PATTERN_REGEX.lookupAssessmentSection,
  },
  {
    apiPath: OFFLINE_APIS.lookupVisitPage,
    pattern: APIS_PATTERN_REGEX.lookupVisitPage,
  },
];

export function isNewOfflineVisit(id) {
  return id >= OFFLINE_ASSESSMENT_ID_INITIAL;
}

export function getUnplannedPatientName(patientId) {
  return `Unplanned (${patientId})`;
}

export function isUnplannedPatient(name) {
  return name.indexOf("Unplanned (") === 0;
}

export function stringifyJSON(json: any, defaultValue: string = "{}") {
  let str;
  try {
    str = JSON.stringify(json);
  } catch (e) {
    str = defaultValue;
  }
  return str;
}

export function parseJSON(str: any, defaultValue: any = {}) {
  let json;
  try {
    json = JSON.parse(str);
  } catch (e) {
    json = defaultValue;
  }
  return json;
}

export function isJSON(str: any) {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}

export function prepareOfflineActionToSync(action: any): any {
  let syncData = {
    actionId: action.action_id,
    patientId: action.patient_id,
    forcedUpdate: false,
    requestMethod: action.action === "DELETE" ? "DELETE" : "UPDATE",
    assessmentId: action.action === "CREATE" ? null : action.assessment_id,
    patientName: action.patient_name,
    roleLookupCode: action.role_lookup_code,
    asmtTypeLookupCode: action.asmt_type_lookup_code,
  };

  let syncChanges: any = {};
  if (action.payload) {
    syncChanges = parseJSON(action.payload);
    syncChanges.detailList.forEach((element) => {
      for (let cardID in element.data) {
        element.data[cardID] = stringifyJSON(element.data[cardID]);
      }
    });
    syncChanges.popupList.forEach((element) => {
      element.data = stringifyJSON(element.data);
    });
  } else {
    syncChanges = {
      detailList: [],
      popupList: [],
    };
  }
  return { ...syncData, ...syncChanges };
}

export const ASSESSMENT_STATUS_CODE = {
  CREATED: "CL004.1",
  IN_PROGRESS: "CL004.2",
  COMPLETED: "CL004.3",
  NOT_STARTED: "CL004.4",
};

export const ASSESSMENT_STATUS_LOOKUP = {
  "CL004.1": "Created",
  "CL004.2": "In Progress",
  "CL004.3": "Completed",
  "CL004.4": "Not Started",
};

export const ASSESSMENT_TYPE_LOOKUP = {
  "CL003.1": "Initial/Comprehensive Assessment",
  "CL003.2": "Comprehensive Assessment",
  "CL003.3": "Routine",
  "CL003.4": "Face to Face Visit",
  "CL003.5": "Continuous Care Visits",
  "CL003.6": "Emergent",
  "CL003.7": "In-Patient",
  "CL003.8": "Watch Care Visit",
  "CL003.9": "EMC",
  "CL003.10": "Phone Call",
  "CL003.11": "Supervisory Visit",
  "CL003.12": "Initial Assessment",
  "CL003.13": "Hospice House Comprehensive",
  "CL003.14": "Hospice House Encounter",
  "CL003.15": "New Patient/Admit",
  "CL003.16": "Established",
};

export const ROLE_LOOKUP = {
  "SD002.1": "Case Manager",
  "SD002.2": "Team Lead",
  "SD002.3": "Registered Nurse (RN)",
  "SD002.4": "Nurse Practitioner",
  "SD002.5": "Hospice Aide",
  "SD002.6": "Homemaker",
  "SD002.7": "Social Worker",
  "SD002.8": "Spiritual Care",
  "SD002.9": "Bereavement",
  "SD002.10": "Medical Director",
  "SD002.11": "LPN/LVN Nurse",
  "SD002.12": "Volunteer Coordinator",
  "SD002.13": "Volunteer",
  OTH: "Other",
};

export const SECTION_CODE_SKIN = "CL001.12";
export const SECTION_CODE_PAIN = "CL001.6";

export const ASSESSMENT_SECTION_TYPE_LOOKUP = {
  "CL001.9": "gastrointestinal",
  "CL001.8": "cardiovascular",
  "CL001.7": "respiratory",
  "CL001.10": "genitourinary",
  [SECTION_CODE_SKIN]: "skin",
  [SECTION_CODE_PAIN]: "pain",
  "CL001.11": "nutritionalMetabolic",
  "CL001.5": "neurological",
  "CL001.13": "musculoskeletal",
  "CL001.27": "adlsFunctionalNeeds",
  "CL001.21": "hospiceAide",
  "CL001.22": "homemaker",
  "CL001.18": "socialWork",
  "CL001.23": "summary",
  "CL001.15": "precautionsSafetyTeachings",
  "CL001.2": "levelOfCare",
  "CL001.26": "preferences",
  "CL001.4": "vitals",
  "CL001.17": "faceToFace",
  "CL001.28": "faceToFaceMD",
  "CL001.24": "volunteer",
  "CL001.16": "supervisoryVisit",
  "CL001.20": "bereavement",
  "CL001.19": "spiritualCare",
  "CL001.29": "billing",
  "CL001.30": "hospiceAideHACarePlan",
  "CL001.31": "militaryHistory",
  navigation: "navigation",
};

export const TEAM_ROLE_GROUP_ENUM = {
  "Case Manager": "Nurse",
  "Team Lead": "Nurse",
  "Registered Nurse (RN)": "Nurse",
  "Nurse Practitioner": "Nurse",
  "Medical Director": "Nurse",
  "LPN/LVN Nurse": "Nurse",
  "Hospice Aide": "Hospice Aide",
  Homemaker: "Homemaker",
  "Social Worker": "Social Worker",
  "Spiritual Care": "Spiritual Care",
  Bereavement: "Bereavement",
  "Volunteer Coordinator": "Volunteer Coordinator",
  Volunteer: "Volunteer",
};

export const LEVEL_OF_CARE = {
  "SD001.1": "Routine Home Care",
  "SD001.2": "GIP",
  "SD001.3": "Continuous",
  "SD001.4": "Respite",
  "SD001.5": "Palliative Care",
};



// WEBPACK FOOTER //
// ./src/providers/offline-manager/offline-globals.ts